// Mobs editor — list + form with loot table sub-editor. Mirrors
// items-screen.ts in shape; "Publish" sends the full draft array.
// Master/detail shell delegated to `createCatalogueEditor`.

import {
  type LootEntry,
  type MobAiType,
  type MobTemplate,
  MobTemplateSchema,
} from "@vinyl/shared";
import {
  type CatalogueEditor,
  createCatalogueEditor,
} from "./catalogue-editor.js";
import { createCombobox } from "./combobox.js";
import { el } from "./dom.js";
import {
  numberField,
  selectField,
  textField,
} from "./form-fields.js";

export interface MobsScreenCallbacks {
  // Item-id list pulled from the currently-published items catalogue. The
  // mob editor's loot dropdown reads this so the admin can only target
  // items that actually exist. Refreshed whenever items get re-published.
  getKnownItemIds: () => string[];
  onDirtyChange: (dirty: boolean) => void;
}

export interface MobsScreen {
  root: HTMLElement;
  setMobs(mobs: readonly MobTemplate[]): void;
  getDraft(): MobTemplate[];
  isDirty(): boolean;
  markPublished(): void;
}

const AI_TYPES: MobAiType[] = [
  "melee",
  "meleeFast",
  "meleeShield",
  "rangedKite",
  "meleeHeavy",
];

function blank(): MobTemplate {
  return {
    id: "new_mob",
    name: "New Mob",
    displayTag: "?",
    displayColor: 0x888888,
    level: { min: 1, max: 1 },
    hp: 50,
    damage: { min: 1, max: 3 },
    aiType: "melee",
    aggroRangeTiles: 5,
    leashRadiusTiles: 20,
    spawnRadiusTiles: 4,
    moveSpeedTilesPerSec: 4,
    attackRangeTiles: 1,
    attackCooldownMs: 1500,
    respawnMs: 60_000,
    xpReward: 10,
    loot: { rareBias: 0.05, entries: [] },
    copperDrop: { min: 0, max: 0 },
  };
}

function sanitize(draft: MobTemplate): MobTemplate {
  const spritePath = draft.spritePath?.trim() ?? "";
  return {
    id: draft.id,
    name: draft.name,
    displayTag: draft.displayTag,
    displayColor: draft.displayColor,
    level: { min: draft.level.min, max: Math.max(draft.level.min, draft.level.max) },
    hp: draft.hp,
    damage: { min: draft.damage.min, max: Math.max(draft.damage.min, draft.damage.max) },
    aiType: draft.aiType,
    aggroRangeTiles: draft.aggroRangeTiles,
    leashRadiusTiles: draft.leashRadiusTiles,
    spawnRadiusTiles: draft.spawnRadiusTiles,
    moveSpeedTilesPerSec: draft.moveSpeedTilesPerSec,
    attackRangeTiles: draft.attackRangeTiles,
    attackCooldownMs: draft.attackCooldownMs,
    respawnMs: draft.respawnMs,
    xpReward: draft.xpReward,
    loot: {
      rareBias: draft.loot.rareBias,
      entries: draft.loot.entries.map((e) => ({
        templateId: e.templateId,
        chance: e.chance,
        ...(e.stack ? { stack: { min: e.stack.min, max: Math.max(e.stack.min, e.stack.max) } } : {}),
      })),
    },
    copperDrop: {
      min: draft.copperDrop.min,
      max: Math.max(draft.copperDrop.min, draft.copperDrop.max),
    },
    ...(spritePath ? { spritePath } : {}),
    ...(draft.resists && Object.keys(draft.resists).length > 0
      ? { resists: { ...draft.resists } }
      : {}),
  };
}

export function createMobsScreen(cb: MobsScreenCallbacks): MobsScreen {
  const editor: CatalogueEditor<MobTemplate> = createCatalogueEditor<MobTemplate>(
    {
      entityName: "mob",
      getName: (m) => m.name,
      blank,
      sanitize,
      renderEditor: ({ draft, markDirty, rerender }) =>
        renderForm(draft, markDirty, rerender),
    },
    { onDirtyChange: cb.onDirtyChange },
  );

  function renderForm(
    draft: MobTemplate,
    markDirty: () => void,
    rerender: () => void,
  ): HTMLElement {
    const container = el("div", {});

    // Identity / Level / Movement / Rewards / Resists / Loot
    container.append(
      el("h2", {}, draft.name || "(unnamed)"),
      section("Identity", [
        textField("id (lowercase, _-separated)", draft.id, (v) => {
          draft.id = v;
          markDirty();
        }),
        textField("Name", draft.name, (v) => {
          draft.name = v;
          markDirty();
        }),
        textField("Display tag (1–3 chars)", draft.displayTag, (v) => {
          draft.displayTag = v.slice(0, 3);
          markDirty();
        }),
        colorField("Display color", draft.displayColor, (v) => {
          draft.displayColor = v;
          markDirty();
        }),
        textField(
          "Sprite path (relative to client/public/assets/)",
          draft.spritePath ?? "",
          (v) => {
            const trimmed = v.trim();
            if (trimmed) draft.spritePath = trimmed;
            else delete draft.spritePath;
            markDirty();
          },
        ),
      ]),
      section("Level & combat", [
        numberField("Level min", draft.level.min, (v) => {
          draft.level.min = Math.max(1, Math.floor(v));
          markDirty();
        }),
        numberField("Level max", draft.level.max, (v) => {
          draft.level.max = Math.max(1, Math.floor(v));
          markDirty();
        }),
        numberField("HP", draft.hp, (v) => {
          draft.hp = Math.max(1, Math.floor(v));
          markDirty();
        }),
        numberField("Damage min", draft.damage.min, (v) => {
          draft.damage.min = Math.max(0, Math.floor(v));
          markDirty();
        }),
        numberField("Damage max", draft.damage.max, (v) => {
          draft.damage.max = Math.max(0, Math.floor(v));
          markDirty();
        }),
        selectField("AI type", AI_TYPES, draft.aiType, (v) => {
          draft.aiType = v;
          markDirty();
        }),
        numberField("Attack range (tiles)", draft.attackRangeTiles, (v) => {
          draft.attackRangeTiles = Math.max(1, Math.floor(v));
          markDirty();
        }),
        numberField("Attack cooldown (ms)", draft.attackCooldownMs, (v) => {
          draft.attackCooldownMs = Math.max(100, Math.floor(v));
          markDirty();
        }),
      ]),
      section("Movement & aggro", [
        numberField("Aggro range (tiles)", draft.aggroRangeTiles, (v) => {
          draft.aggroRangeTiles = Math.max(0, Math.floor(v));
          markDirty();
        }),
        numberField("Leash radius (tiles)", draft.leashRadiusTiles, (v) => {
          draft.leashRadiusTiles = Math.max(0, Math.floor(v));
          markDirty();
        }),
        numberField("Spawn radius (tiles)", draft.spawnRadiusTiles, (v) => {
          draft.spawnRadiusTiles = Math.max(0, Math.floor(v));
          markDirty();
        }),
        numberField(
          "Move speed (tiles/sec)",
          draft.moveSpeedTilesPerSec,
          (v) => {
            draft.moveSpeedTilesPerSec = Math.max(0, v);
            markDirty();
          },
          0.5,
        ),
        numberField("Respawn (ms)", draft.respawnMs, (v) => {
          draft.respawnMs = Math.max(1_000, Math.floor(v));
          markDirty();
        }),
      ]),
      section("Rewards", [
        numberField("XP reward", draft.xpReward, (v) => {
          draft.xpReward = Math.max(0, Math.floor(v));
          markDirty();
        }),
        numberField("Copper drop min", draft.copperDrop.min, (v) => {
          draft.copperDrop.min = Math.max(0, Math.floor(v));
          markDirty();
        }),
        numberField("Copper drop max", draft.copperDrop.max, (v) => {
          draft.copperDrop.max = Math.max(0, Math.floor(v));
          markDirty();
        }),
      ]),
      renderResistsSection(draft, markDirty),
      renderLootSection(draft, markDirty, rerender),
    );

    // Live validation feedback panel.
    const validationEl = el("div", { class: "error" });
    function refreshValidation(): void {
      const parsed = MobTemplateSchema.safeParse(sanitize(draft));
      validationEl.textContent = parsed.success
        ? ""
        : "Invalid: " +
          parsed.error.issues.map((i) => `${i.path.join(".")} ${i.message}`).join("; ");
    }
    refreshValidation();
    container.addEventListener("input", refreshValidation);
    container.addEventListener("change", refreshValidation);

    container.append(
      el("div", { class: "editor-section" }, el("h3", {}, "Validation"), validationEl),
      el(
        "div",
        { class: "editor-actions" },
        el(
          "button",
          {
            class: "button danger",
            onclick: () => {
              if (!confirm(`Delete "${draft.name}" from the draft? Publish to apply.`)) return;
              const next = editor.getDraft().filter((d) => d.id !== draft.id);
              editor.set(next);
              cb.onDirtyChange(true);
            },
          },
          "Delete from draft",
        ),
      ),
    );

    return container;
  }

  // M8 — per-school resists, all optional. UI: one numeric input per school
  // (0..0.95, expressed as 0..1 fraction). Empty / 0 entries are dropped at
  // sanitize time so a freshly-authored mob with no resists doesn't bloat
  // the JSON.
  function renderResistsSection(draft: MobTemplate, markDirty: () => void): HTMLElement {
    type ResistKey = keyof NonNullable<MobTemplate["resists"]>;
    const keys: ResistKey[] = [
      "physical",
      "divine",
      "necromancy",
      "water",
      "fire",
      "air",
      "earth",
    ];
    const fields: HTMLElement[] = [];
    for (const k of keys) {
      const cur = draft.resists?.[k] ?? 0;
      fields.push(
        numberField(
          `${k} (0–0.95)`,
          cur,
          (v) => {
            const clamped = Math.max(0, Math.min(0.95, v));
            if (!draft.resists) draft.resists = {};
            if (clamped > 0) draft.resists[k] = clamped;
            else delete draft.resists[k];
            // If the whole block ended up empty, drop it for a tidy JSON.
            if (Object.keys(draft.resists).length === 0) delete draft.resists;
            markDirty();
          },
          0.05,
        ),
      );
    }
    return el(
      "div",
      { class: "editor-section" },
      el("h3", {}, "Resists (per magic school)"),
      el("div", { class: "editor-grid" }, ...fields),
      el(
        "div",
        { class: "affix-hint" },
        "Each value is 0..0.95 (capped). Physical applies to melee/ranged; the 6 magic schools apply to matching spell damage.",
      ),
    );
  }

  function renderLootSection(
    draft: MobTemplate,
    markDirty: () => void,
    rerender: () => void,
  ): HTMLElement {
    const knownIds = cb.getKnownItemIds();
    const entriesList = el("div", { class: "affix-list" });

    for (let idx = 0; idx < draft.loot.entries.length; idx++) {
      entriesList.append(renderLootRow(draft, idx, knownIds, markDirty, rerender));
    }

    const addBtn = el(
      "button",
      {
        class: "button secondary",
        type: "button",
        onclick: () => {
          const fallback = knownIds[0] ?? "rusty_dagger";
          draft.loot.entries = [
            ...draft.loot.entries,
            { templateId: fallback, chance: 0.1 },
          ];
          markDirty();
          rerender();
        },
      },
      "+ Add loot entry",
    );

    const rareBiasInput = numberField(
      "rareBias (0–1)",
      draft.loot.rareBias,
      (v) => {
        draft.loot.rareBias = Math.max(0, Math.min(1, v));
        markDirty();
      },
      0.01,
    );

    return el(
      "div",
      { class: "editor-section" },
      el("h3", {}, `Loot table (${draft.loot.entries.length}/20)`),
      el("div", { class: "editor-grid" }, rareBiasInput),
      entriesList,
      el("div", { class: "affix-actions" }, addBtn),
      el(
        "div",
        { class: "affix-hint" },
        "Each entry rolls independently. ",
        "Chance is the per-drop probability (0–1). Stackable items can specify a stack range.",
      ),
    );
  }

  function renderLootRow(
    draft: MobTemplate,
    idx: number,
    knownIds: readonly string[],
    markDirty: () => void,
    rerender: () => void,
  ): HTMLElement {
    const entry = draft.loot.entries[idx]!;
    const row = el("div", { class: "loot-row" });

    // Item picker — typeable combobox driven by the live items catalogue.
    row.append(
      createCombobox({
        options: knownIds,
        value: entry.templateId,
        onChange: (v) => {
          const updated: LootEntry = { ...entry, templateId: v };
          draft.loot.entries = draft.loot.entries.map((x, i) => (i === idx ? updated : x));
          markDirty();
        },
        placeholder: "Search items…",
      }),
    );

    // Chance
    const chanceInput = el("input", {
      type: "number",
      value: String(entry.chance),
      step: "0.01",
      min: "0",
      max: "1",
      oninput: (e: Event) => {
        const v = Number.parseFloat((e.target as HTMLInputElement).value);
        if (Number.isFinite(v)) {
          const clamped = Math.max(0, Math.min(1, v));
          draft.loot.entries = draft.loot.entries.map((x, i) =>
            i === idx ? { ...x, chance: clamped } : x,
          );
          markDirty();
        }
      },
    });
    row.append(chanceInput);

    // Stack toggle + min/max
    const hasStack = !!entry.stack;
    const toggle = el("input", {
      type: "checkbox",
      onchange: (e: Event) => {
        const checked = (e.target as HTMLInputElement).checked;
        const updated: LootEntry = checked
          ? { ...entry, stack: entry.stack ?? { min: 1, max: 1 } }
          : { templateId: entry.templateId, chance: entry.chance };
        draft.loot.entries = draft.loot.entries.map((x, i) => (i === idx ? updated : x));
        markDirty();
        rerender();
      },
    });
    if (hasStack) (toggle as HTMLInputElement).checked = true;
    row.append(el("label", { class: "loot-stack-toggle" }, toggle, "stack"));

    if (hasStack && entry.stack) {
      const stack = entry.stack;
      row.append(
        el("input", {
          type: "number",
          value: String(stack.min),
          min: "1",
          step: "1",
          oninput: (e: Event) => {
            const v = Math.max(1, Number.parseInt((e.target as HTMLInputElement).value, 10) || 1);
            draft.loot.entries = draft.loot.entries.map((x, i) =>
              i === idx ? { ...x, stack: { ...stack, min: v } } : x,
            );
            markDirty();
          },
        }),
        el("input", {
          type: "number",
          value: String(stack.max),
          min: "1",
          step: "1",
          oninput: (e: Event) => {
            const v = Math.max(1, Number.parseInt((e.target as HTMLInputElement).value, 10) || 1);
            draft.loot.entries = draft.loot.entries.map((x, i) =>
              i === idx ? { ...x, stack: { ...stack, max: v } } : x,
            );
            markDirty();
          },
        }),
      );
    } else {
      // Keep the grid layout consistent — 2 empty slots when stack is off.
      row.append(el("span"), el("span"));
    }

    const removeBtn = el(
      "button",
      {
        class: "button danger",
        type: "button",
        title: "Remove entry",
        onclick: () => {
          draft.loot.entries = draft.loot.entries.filter((_, i) => i !== idx);
          markDirty();
          rerender();
        },
      },
      "×",
    );
    row.append(removeBtn);

    return row;
  }

  return {
    root: editor.root,
    setMobs(mobs) {
      editor.set(
        mobs.map((m) => ({
          ...m,
          loot: {
            rareBias: m.loot.rareBias,
            entries: m.loot.entries.map((e) => ({ ...e })),
          },
          level: { ...m.level },
          damage: { ...m.damage },
          copperDrop: { ...m.copperDrop },
          ...(m.resists ? { resists: { ...m.resists } } : {}),
        })),
      );
    },
    getDraft: editor.getDraft,
    isDirty: editor.isDirty,
    markPublished: editor.markPublished,
  };
}

// Local helpers.

function section(title: string, children: HTMLElement[]): HTMLElement {
  return el(
    "div",
    { class: "editor-section" },
    el("h3", {}, title),
    el("div", { class: "editor-grid" }, ...children),
  );
}

// Color picker — kept local since only the mobs editor uses one.
function colorField(
  label: string,
  value: number,
  onChange: (v: number) => void,
): HTMLElement {
  const hex = "#" + value.toString(16).padStart(6, "0");
  const input = el("input", {
    type: "color",
    value: hex,
    oninput: (e: Event) => {
      const raw = (e.target as HTMLInputElement).value;
      const parsed = Number.parseInt(raw.replace("#", ""), 16);
      if (Number.isFinite(parsed)) onChange(parsed);
    },
  });
  return el("div", { class: "field" }, el("label", {}, label), input);
}
